import { useInfiniteQuery } from '@tanstack/react-query';
import { forwardRef, useId, type JSX } from 'react';

import { Select } from '@/components/ui/Select';

import styles from './TenantModulesTab.module.scss';
import {
  getModuleHistory,
  moduleHistoryQueryKey,
  type TenantModule,
} from '../api/tenant-modules.graphql';
import { formatDateTime } from '../module-labels';

const PAGE_SIZE = 20;

interface Props {
  tenantId: string;
  modules: TenantModule[];
  presetNames: ReadonlyMap<string, string>;
  /** Módulo filtrado; `null` = todos. */
  moduleKey: string | null;
  onModuleKeyChange: (moduleKey: string | null) => void;
}

/** OS-1716 — historial de cambios de módulos (quién, cuándo, motivo) leído de la auditoría. */
export const ModuleHistoryPanel = forwardRef<HTMLHeadingElement, Props>(function ModuleHistoryPanel(
  { tenantId, modules, presetNames, moduleKey, onModuleKeyChange },
  headingRef,
): JSX.Element {
  const filterId = useId();
  const names = new Map(modules.map((m) => [m.key, m.name]));
  const history = useInfiniteQuery({
    queryKey: moduleHistoryQueryKey(tenantId, moduleKey),
    queryFn: ({ pageParam }) =>
      getModuleHistory(tenantId, { take: PAGE_SIZE, cursor: pageParam, moduleKey }),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextCursor,
  });
  const entries = history.data?.pages.flatMap((page) => page.entries) ?? [];

  return (
    <section className={styles.history} aria-labelledby={`${filterId}-title`}>
      <div className={styles.historyHeader}>
        <h3 id={`${filterId}-title`} ref={headingRef} tabIndex={-1} className={styles.sectionTitle}>
          Historial de cambios
        </h3>
        <label className={styles.filter} htmlFor={filterId}>
          Módulo
          <Select
            id={filterId}
            value={moduleKey ?? ''}
            onChange={(value) => onModuleKeyChange(value || null)}
            options={[
              { value: '', label: 'Todos los módulos' },
              ...modules.map((m) => ({ value: m.key, label: m.name })),
            ]}
          />
        </label>
      </div>

      {history.isLoading ? (
        <p className={styles.muted} role="status">Cargando historial…</p>
      ) : history.isError ? (
        <div className={styles.errorBox} role="alert">
          <span>No se pudo cargar el historial: {history.error.message}</span>
          <button type="button" className={styles.linkBtn} onClick={() => void history.refetch()}>
            Reintentar
          </button>
        </div>
      ) : entries.length === 0 ? (
        <p className={styles.muted}>
          {moduleKey
            ? `Sin cambios registrados para ${names.get(moduleKey) ?? moduleKey}.`
            : 'Sin cambios de módulos registrados todavía.'}
        </p>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-labelledby={`${filterId}-title`}>
            <thead>
              <tr>
                <th scope="col">Fecha</th>
                <th scope="col">Módulo</th>
                <th scope="col">Cambio</th>
                <th scope="col">Motivo</th>
                <th scope="col">Por</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr key={entry.id}>
                  <td>{formatDateTime(entry.createdAt)}</td>
                  <td>{names.get(entry.moduleKey) ?? entry.moduleKey}</td>
                  <td>
                    <span className={entry.enabled ? styles.on : styles.off}>
                      {entry.enabled ? 'Habilitado' : 'Deshabilitado'}
                    </span>
                    {entry.preset ? (
                      <span className={styles.muted}>
                        {' '}
                        · preset {presetNames.get(entry.preset) ?? entry.preset}
                      </span>
                    ) : null}
                  </td>
                  <td>{entry.reason ?? '—'}</td>
                  <td>{entry.adminEmail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {history.hasNextPage ? (
        <button
          type="button"
          className={styles.secondaryBtn}
          disabled={history.isFetchingNextPage}
          onClick={() => void history.fetchNextPage()}
        >
          {history.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
        </button>
      ) : null}
    </section>
  );
});
